// The GlossScroll overlay scrollbar, framework-free so it can be put on any scroll container: the
// element keeps scrolling itself (its scroll listeners, scrollIntoView and sticky children keep
// working), its native bar is hidden, and thin overlay thumbs (vertical and horizontal) show while
// the pointer is over it or it's scrolling, fading out a second after. They grow and darken under
// the pointer, and drag. Only a thumb takes the pointer: the rest of its strip lies over the
// element's own content (a grid's last row, an option's end), which keeps its clicks. The overlay
// is a sibling placed over the element, so it doesn't take layout space or sit in the scrolled
// content, and is clipped by the same ancestors. Styles are the .gs-* rules in base.scss.

// Gap between a thumb and the ends of its track, and the smallest a thumb gets.
const INSET = 4;
const MIN_THUMB = 24;
// A strip's thickness (base.scss), kept out of the other's track where both show.
const STRIP = 12;
// How long the bars stay after the last scroll.
const IDLE_MS = 1000;
// The bars' fade-out (base.scss), after which the overlay is parked.
const FADE_MS = 200;
// Overflow values that clip what's outside the box, and those the user can't scroll.
const CLIPS = /\b(hidden|clip|auto|scroll)\b/;
export const UNSCROLLABLE = /^(hidden|clip|visible)$/;
// Wheel deltas in lines and pages (deltaMode 1 and 2), in px.
const LINE = 16;
// Frames in a row with the element not moving before follow() rests (see the wakes in the
// constructor).
const STILL_FRAMES = 30;

type Axis = "y" | "x";

interface Bar {
  strip: HTMLDivElement;
  thumb: HTMLDivElement;
}

interface Drag {
  axis: Axis;
  // The pointer dragging: another one's moves and ups are not the drag's.
  pointerId: number;
  pointer: number;
  scroll: number;
  ratio: number;
}

/** Touch-only devices keep their OS overlay scrollbar, which already hides itself. */
export function glossScrollEnhanced(): boolean {
  return !window.matchMedia?.("(hover: none) and (pointer: coarse)").matches;
}

export class GlossScrollbar {
  // Made the first time a bar shows: many hosts never overflow, or are never hovered.
  private overlay?: HTMLDivElement;
  private bars?: Record<Axis, Bar>;
  private hostHovered = false;
  private overlayHovered: Axis | null = null;
  private drag: Drag | null = null;
  private scrolling = false;
  private shown = false;
  private overflow: Record<Axis, boolean> = { y: false, x: false };
  private rtl = false;
  // The element is inert or takes no pointer events (e.g. while a form submits), so its bar
  // doesn't either (the overlay isn't inside it to inherit that).
  private disabled = false;
  // Both bars show, so each keeps out of the other's corner.
  private cornered = false;
  // Where the element was on screen when the overlay was last placed.
  private placedAt = { left: NaN, top: NaN };
  // The element's positioning and clipping ancestors, read when the bars come into view and kept
  // while they're up (they don't change as it moves).
  private position = "";
  private zIndex = "";
  private clippers: HTMLElement[] = [];
  // Content changes, watched only while the bars are up.
  private content: MutationObserver | null = null;
  private timer?: ReturnType<typeof setTimeout>;
  private parkTimer?: ReturnType<typeof setTimeout>;
  private frame = 0;
  // The element's own user-select, put back after a drag.
  private userSelect = "";
  // Found disabled when last wanted: not re-measured on every scroll event until it's let go.
  private disabledWhenWanted = false;
  // A content change re-measures once a frame, however many mutations it took.
  private pending = 0;
  // A coalesced re-measure is to lay the overlay again (not only content changed).
  private relay = false;
  private contentWatched = false;
  private resize: ResizeObserver | null = null;
  // follow() stopped with the element still: what may move it restarts it (below).
  private resting = false;
  // Every listener goes with it; observers disconnect on its abort.
  private readonly abort = new AbortController();

  /**
   * `enhanced: false` (touch) draws nothing and leaves the native bar, but still reports overflow.
   * `onOverflow` hears whether the element can scroll at all (either axis).
   */
  constructor(
    private readonly el: HTMLElement,
    private readonly options: { enhanced: boolean; onOverflow?: (overflowing: boolean) => void },
  ) {
    if (options.enhanced) {
      // gs-host hides the native bar over any other scrollbar styling (base.scss).
      el.classList.add("gs-host");
      // pointerover, not pointerenter: it says what in the element the pointer is over.
      this.listen(el, "pointerover", (event) => this.setHostHovered(!this.escapes(event.target)));
      this.listen(el, "pointerleave", () => this.setHostHovered(false));
      this.listen(el, "scroll", () => this.onScroll(), { passive: true });
      // While follow() rests, what may have moved the element wakes it: an ancestor (or the page)
      // scrolling, heard in the capture phase as scroll events don't bubble; the window resizing;
      // and the pointer moving, or a key pressed, anywhere, which catches the rest (e.g. content
      // above it growing, from a click or Enter): the pointer moves to whatever it presses, so the
      // thumb, which takes the pointer, is back on the element by then rather than left over other
      // content. Content moving it with no input at all is caught up with at the next of these. A
      // cheap check otherwise.
      const wake = () => this.resting && this.follow();
      this.listen(
        document,
        "scroll",
        (event) => {
          const target = event.target;
          if (target === document || (target instanceof Node && target.contains(el))) {
            wake();
          }
        },
        { capture: true, passive: true },
      );
      this.listen(window, "resize", wake, { passive: true });
      this.listen(document, "pointermove", wake, { capture: true, passive: true });
      this.listen(document, "keydown", wake, { capture: true, passive: true });
    }
    this.observe();
    this.update();
  }

  /** Re-measures the content and the element (call after changing its size from outside). */
  update(): void {
    this.measure(true);
  }

  // `relay`: lay the overlay again too, for a change that may move or resize the element (not one
  // inside its content: follow() and the ResizeObserver see those).
  private measure(relay: boolean): void {
    const { scrollHeight, clientHeight, scrollWidth, clientWidth } = this.el;
    // An axis that's clipped or visible (e.g. at a breakpoint where the element doesn't scroll)
    // gets no bar, though its content may be bigger than the box.
    const style = getComputedStyle(this.el);
    const overflow = {
      y: !UNSCROLLABLE.test(style.overflowY) && scrollHeight - clientHeight > 1,
      x: !UNSCROLLABLE.test(style.overflowX) && scrollWidth - clientWidth > 1,
    };
    this.rtl = style.direction === "rtl";
    const moved = style.position !== this.position;
    this.position = style.position;
    this.zIndex = style.zIndex;
    this.disabled = style.pointerEvents === "none" || !!this.el.closest("[inert]");
    const roomy = (size: number) => size - INSET * 2 - STRIP >= MIN_THUMB;
    this.cornered = overflow.x && overflow.y && roomy(clientHeight) && roomy(clientWidth);
    const was = this.overflow.x || this.overflow.y;
    this.overflow = overflow;
    if (was !== (overflow.x || overflow.y)) {
      this.options.onOverflow?.(overflow.x || overflow.y);
    }
    this.refresh(true, moved, relay);
  }

  destroy(): void {
    clearTimeout(this.timer);
    clearTimeout(this.parkTimer);
    cancelAnimationFrame(this.frame);
    cancelAnimationFrame(this.pending);
    this.abort.abort();
    this.overlay?.remove();
    this.el.classList.remove("gs-host");
    if (this.drag) {
      this.el.style.userSelect = this.userSelect;
    }
  }

  private createOverlay(): void {
    if (this.overlay) {
      return;
    }
    const overlay = document.createElement("div");
    overlay.className = "gs-overlay";
    overlay.setAttribute("aria-hidden", "true");
    this.overlay = overlay;
    this.bars = { y: this.createBar("y"), x: this.createBar("x") };
    this.el.insertAdjacentElement("afterend", overlay);
    // The overlay isn't inside the element, so a wheel over a bar would scroll its ancestors.
    this.listen(overlay, "wheel", (event) => this.onWheel(event as WheelEvent), {
      passive: false,
    });
    // Pressing a thumb keeps the focus where it is (pointerdown's preventDefault doesn't).
    this.listen(overlay, "mousedown", (event) => event.preventDefault());
  }

  private createBar(axis: Axis): Bar {
    const strip = document.createElement("div");
    strip.className = "gs-strip";
    strip.dataset.axis = axis;
    const thumb = document.createElement("div");
    thumb.className = "gs-thumb";
    strip.appendChild(thumb);
    this.overlay!.appendChild(strip);

    this.listen(thumb, "pointerenter", () => this.setOverlayHovered(axis));
    this.listen(thumb, "pointerleave", () => this.setOverlayHovered(null));
    this.listen(thumb, "pointerdown", (event) => this.onThumbDown(axis, event as PointerEvent));
    this.listen(thumb, "pointermove", (event) => this.onThumbMove(event as PointerEvent));
    const endDrag = (event: Event) => {
      if (!this.drag || (event as PointerEvent).pointerId !== this.drag.pointerId) {
        return;
      }
      this.drag = null;
      this.el.style.userSelect = this.userSelect;
      this.showOrHide();
    };
    this.listen(thumb, "pointerup", endDrag);
    this.listen(thumb, "pointercancel", endDrag);
    // Capture can go without a pointerup (e.g. the thumb is taken out from under the pointer).
    this.listen(thumb, "lostpointercapture", endDrag);
    return { strip, thumb };
  }

  private listen(
    target: EventTarget,
    type: string,
    handler: (event: Event) => void,
    options?: AddEventListenerOptions,
  ): void {
    target.addEventListener(type, handler, { ...options, signal: this.abort.signal });
  }

  // Resizes of the element, of any child, or of an ancestor clipping it change the overflow or
  // the clip; its own inert and classes, whether it takes the pointer (an ancestor's are read
  // when the bars are next wanted). Inline style writes aren't watched: transforms and the like
  // come every frame, and sizes resize.
  private observe(): void {
    const resize =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => this.update());
    this.resize = resize;
    // Not a nested host's overlay, which isn't content (and comes and goes with its bars).
    const isContent = (node: Node): node is Element =>
      node instanceof Element && !node.classList.contains("gs-overlay");
    resize?.observe(this.el);
    Array.from(this.el.children)
      .filter(isContent)
      .forEach((child) => resize?.observe(child));
    // The children that come and go are watched for size (not all of them again, lists can be
    // long), and change the overflow themselves.
    const children = new MutationObserver((records) => {
      for (const record of records) {
        record.removedNodes.forEach((node) => {
          if (node instanceof Element) {
            resize?.unobserve(node);
          }
        });
        record.addedNodes.forEach((node) => {
          if (isContent(node) && node.parentNode === this.el) {
            resize?.observe(node);
          }
        });
      }
      this.updateSoon();
    });
    children.observe(this.el, { childList: true });
    const state = new MutationObserver(() => this.updateSoon(true));
    // data-scroll-locked: ScrollLockDirective holds the page with an inline overflow: hidden.
    state.observe(this.el, {
      attributes: true,
      attributeFilter: ["inert", "class", "data-scroll-locked"],
    });
    this.abort.signal.addEventListener("abort", () => {
      resize?.disconnect();
      children.disconnect();
      state.disconnect();
      this.content?.disconnect();
    });
  }

  // Content can change without resizing anything watched: a <pre>'s text replaced, rows inside
  // an inline child (whose box doesn't count). That matters only to a bar that's up (one coming
  // up re-measures anyway), so the whole subtree is watched only then.
  private watchContent(on: boolean): void {
    if (on === this.contentWatched) {
      return;
    }
    this.contentWatched = on;
    if (!on) {
      this.content?.disconnect();
      return;
    }
    this.content ??= new MutationObserver(() => this.updateSoon(false, false));
    this.content.observe(this.el, { childList: true, characterData: true, subtree: true });
  }

  // Once a frame. Content changes not while the bars are down and no one hears the overflow:
  // they re-measure as they come up; `always` for the element's state.
  private updateSoon(always = false, relay = true): void {
    this.relay ||= relay;
    if (!this.pending && (always || this.shown || this.options.onOverflow)) {
      this.pending = requestAnimationFrame(() => {
        this.pending = 0;
        const relayNow = this.relay;
        this.relay = false;
        this.measure(relayNow);
      });
    }
  }

  private setHostHovered(hovered: boolean): void {
    if (hovered !== this.hostHovered) {
      this.hostHovered = hovered;
      if (hovered) {
        this.disabledWhenWanted = false; // an ancestor may have let go meanwhile
      }
      this.showOrHide();
    }
  }

  // Over something in the element that's fixed (a full-screen panel, and the panel's own bars),
  // the pointer isn't over the element as such. Its bar, drawn above all of its content, would
  // cover the panel and its bar, and scroll the element hidden behind the panel. A fixed panel
  // is positioned, so it's on the target's offsetParent chain, which is short (positioned
  // ancestors only); there, a fixed element has no offsetParent (or <body>, in older Firefox),
  // and only then is its style read.
  private escapes(target: EventTarget | null): boolean {
    let node: Element | null = target instanceof Element ? target : null;
    while (node && !(node instanceof HTMLElement)) {
      node = node.parentElement; // an icon's SVG
    }
    for (let el = node as HTMLElement | null; el && el !== this.el && this.el.contains(el); ) {
      const parent = el.offsetParent as HTMLElement | null;
      if ((!parent || parent === document.body) && getComputedStyle(el).position === "fixed") {
        return true;
      }
      el = parent;
    }
    return false;
  }

  private setOverlayHovered(axis: Axis | null): void {
    this.overlayHovered = axis;
    this.showOrHide();
  }

  // A wheel over a thumb acts as over the element. Its own wheel listeners (and its ancestors') hear
  // it first, as a copy sent from the element, and may take it; else the element scrolls, and what
  // it can't take (at an end) goes on to its ancestors, unless its overscroll-behavior on that axis
  // keeps it. Ctrl+wheel, and a trackpad pinch, is the browser's zoom.
  private onWheel(event: WheelEvent): void {
    if (event.ctrlKey) {
      return;
    }
    const el = this.el;
    event.stopPropagation(); // the copy bubbles to the same ancestors
    const copy = new WheelEvent("wheel", {
      bubbles: true,
      cancelable: true,
      composed: true,
      deltaX: event.deltaX,
      deltaY: event.deltaY,
      deltaZ: event.deltaZ,
      deltaMode: event.deltaMode,
      clientX: event.clientX,
      clientY: event.clientY,
      screenX: event.screenX,
      screenY: event.screenY,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      metaKey: event.metaKey,
      buttons: event.buttons,
    });
    el.dispatchEvent(copy);
    if (copy.defaultPrevented) {
      event.preventDefault();
      return;
    }
    const page = { x: el.clientWidth, y: el.clientHeight };
    // Shift+wheel is sideways. Chromium and macOS send it as deltaX already; where it still comes
    // as deltaY, it's taken sideways here.
    const sideways = event.shiftKey && !event.deltaX;
    const delta = (axis: Axis) => {
      const raw =
        axis === "y" ? (sideways ? 0 : event.deltaY) : sideways ? event.deltaY : event.deltaX;
      const unit = event.deltaMode === 1 ? LINE : event.deltaMode === 2 ? page[axis] : 1;
      return this.overflow[axis] ? raw * unit : 0;
    };
    const left = delta("x");
    const top = delta("y");
    const before = [el.scrollLeft, el.scrollTop];
    if (left || top) {
      // Instant whatever the element's scroll-behavior: a smooth scroll per wheel event would
      // keep restarting, and the position couldn't be compared just below.
      el.scrollBy({ left, top, behavior: "instant" });
    }
    const style = getComputedStyle(el);
    const keeps = (axis: Axis, value: string | undefined) =>
      !!delta(axis) && /^(contain|none)$/.test(value ?? "");
    if (
      el.scrollLeft !== before[0] ||
      el.scrollTop !== before[1] ||
      keeps("y", style.overscrollBehaviorY) ||
      keeps("x", style.overscrollBehaviorX)
    ) {
      event.preventDefault();
    }
  }

  // While the bars are up, the element may move without resizing (an ancestor scrolls, content
  // above it grows), so it's followed frame by frame; one read of its box, for one element. Still
  // for STILL_FRAMES frames, it rests until something may have moved it (the wakes in the
  // constructor; a re-measure or scroll of its own follows again too), rather than ask for frames
  // while nothing moves: the page's own scroller is hovered all the time the pointer's on the page.
  private follow(): void {
    this.resting = false;
    if (this.frame) {
      return;
    }
    let still = 0;
    const tick = () => {
      this.frame = 0;
      // On through the fade, until parked: a fading thumb left behind would float there.
      if (!this.overlay || this.overlay.style.display === "none" || !this.overflows()) {
        return;
      }
      const { left, top } = this.el.getBoundingClientRect();
      if (left !== this.placedAt.left || top !== this.placedAt.top) {
        this.place(false);
        still = 0;
      } else if (++still >= STILL_FRAMES) {
        this.resting = true;
        return;
      }
      this.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
  }

  private onScroll(): void {
    // It scrolls, so something overflows: content may have changed without resizing anything the
    // observers watch (e.g. the text of a fixed-height <pre>).
    if (this.shown && !this.overflows()) {
      this.update();
    }
    this.scrolling = true;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.scrolling = false;
      this.showOrHide();
    }, IDLE_MS);
    this.showOrHide();
  }

  // Re-measure only when the bars come into view (the element may have changed since they last
  // showed); while they stay up, a scroll just moves the thumbs, and follow() keeps them on it.
  private showOrHide(): void {
    // Wanted, not just visible: a bar disabled by an ancestor (inert, no pointer events) learns
    // that it's been let go of here, as nothing watches the ancestors.
    const wanted = this.wanted();
    if (wanted && !this.shown && !this.disabledWhenWanted) {
      this.update();
      this.disabledWhenWanted = this.disabled;
    } else {
      if (!wanted) {
        this.disabledWhenWanted = false;
      }
      this.refresh(false);
    }
  }

  // Whether the bars are up, from the current state; `measured` after a re-measure, which lays
  // the overlay again. With no bar up, the overlay is parked once they've faded.
  // `repositioned`: the element's positioning changed (e.g. fixed below a breakpoint), so its
  // clipping ancestors are read again too.
  private refresh(measured: boolean, repositioned = false, relay = true): void {
    const visible = this.isVisible();
    const was = this.shown;
    this.shown = visible;
    // Up (overflowing or not yet: its text may grow), its content is watched.
    if (visible) {
      this.watchContent(true);
    }
    if (visible && this.overflows()) {
      // Coming up, the element's positioning and clipping ancestors are read afresh; while up, a
      // re-measure (resize, content change) only lays the overlay again.
      if (!was || !this.overlay || this.overlay.style.display === "none" || repositioned) {
        clearTimeout(this.parkTimer);
        this.createOverlay();
        this.place(true);
      } else if (measured && relay) {
        this.place(false);
      }
      this.follow();
    } else if (this.overlay && this.overlay.style.display !== "none") {
      this.parkSoon();
    } else if (!visible) {
      this.watchContent(false);
    }
    this.render();
  }

  // Out of layout while no bar shows: left where it was, the overlay's box would still count
  // towards a scrolling containing block's content after the element shrinks or moves.
  private park(): void {
    if (this.overlay && (!this.shown || !this.overflows())) {
      this.resting = false;
      this.overlay.style.display = "none";
      this.watchClippers([]);
      if (!this.shown) {
        this.watchContent(false);
      }
    }
  }

  // Only those joining and leaving: observing an element again has it reported again, which
  // re-measures, which would observe it again, every frame.
  private watchClippers(clippers: HTMLElement[]): void {
    this.clippers
      .filter((node) => !clippers.includes(node))
      .forEach((node) => this.resize?.unobserve(node));
    clippers
      .filter((node) => !this.clippers.includes(node))
      .forEach((node) => this.resize?.observe(node));
    this.clippers = clippers;
  }

  // Once the bars have faded.
  private parkSoon(): void {
    clearTimeout(this.parkTimer);
    this.parkTimer = setTimeout(() => this.park(), FADE_MS);
  }

  private overflows(): boolean {
    return this.overflow.x || this.overflow.y;
  }

  private isVisible(): boolean {
    return !this.disabled && this.wanted();
  }

  private wanted(): boolean {
    return (
      this.options.enhanced &&
      (this.hostHovered || !!this.overlayHovered || this.scrolling || !!this.drag)
    );
  }

  // Lays the overlay over the element's padding box, wherever its containing block is. It takes
  // the element's stacking (a raised element, e.g. a floating dropdown, would otherwise cover it)
  // and, for a fixed element, fixed positioning too, so it stays put with it.
  // `fresh: false` (following a move, a re-measure while up) keeps the clipping ancestors last
  // read.
  private place(fresh: boolean): void {
    const overlay = this.overlay!;
    const el = this.el;
    overlay.style.display = "";
    // As update() last read them. Not inside the element, so its colours and direction are told
    // rather than inherited.
    overlay.style.position = this.position === "fixed" ? "fixed" : "";
    overlay.style.zIndex = this.zIndex === "auto" || !this.zIndex ? "" : this.zIndex;
    overlay.classList.toggle("gs-dark", el.classList.contains("gs-dark"));
    overlay.style.direction = this.rtl ? "rtl" : "ltr";
    overlay.style.width = `${el.clientWidth}px`;
    overlay.style.height = `${el.clientHeight}px`;
    // At 0,0 the overlay sits on its containing block's origin (left/top auto would leave it at
    // its static position instead), so the distance to the element is its offset. Screen
    // distances are divided by the current scale: an ancestor may be scaling in (a dialog or
    // dropdown opening), and the overlay, inside the same ancestor, scales with it.
    overlay.style.left = "0px";
    overlay.style.top = "0px";
    const origin = overlay.getBoundingClientRect();
    const host = el.getBoundingClientRect();
    const scale = overlay.offsetWidth ? origin.width / overlay.offsetWidth || 1 : 1;
    overlay.style.left = `${(host.left - origin.left) / scale + el.clientLeft}px`;
    overlay.style.top = `${(host.top - origin.top) / scale + el.clientTop}px`;
    this.placedAt = { left: host.left, top: host.top };
    if (fresh) {
      this.watchClippers(this.findClippers());
    }
    // Where the overlay now is on screen: over the element's padding box.
    this.clip(scale, {
      left: host.left + el.clientLeft * scale,
      top: host.top + el.clientTop * scale,
      right: host.left + (el.clientLeft + el.clientWidth) * scale,
      bottom: host.top + (el.clientTop + el.clientHeight) * scale,
    });
  }

  // An element in flow is clipped by its overflow-clipping ancestors, but the overlay only by
  // those from its containing block up (they clip both), so it's clipped to what of the element
  // the ones below leave visible. A positioned element shares the overlay's containing block.
  private findClippers(): HTMLElement[] {
    if (this.position === "absolute" || this.position === "fixed") {
      return [];
    }
    const clippers: HTMLElement[] = [];
    const container = this.overlay!.offsetParent;
    for (let node = this.el.parentElement; node && node !== container; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (CLIPS.test(`${style.overflowX} ${style.overflowY}`)) {
        clippers.push(node);
      }
    }
    return clippers;
  }

  private clip(
    scale: number,
    box: { left: number; top: number; right: number; bottom: number },
  ): void {
    const overlay = this.overlay!;
    overlay.style.clipPath = "";
    if (!this.clippers.length) {
      return;
    }
    let { left, top, right, bottom } = box;
    for (const node of this.clippers) {
      const rect = node.getBoundingClientRect();
      left = Math.max(left, rect.left + node.clientLeft * scale);
      top = Math.max(top, rect.top + node.clientTop * scale);
      right = Math.min(right, rect.left + (node.clientLeft + node.clientWidth) * scale);
      bottom = Math.min(bottom, rect.top + (node.clientTop + node.clientHeight) * scale);
    }
    const inset = [top - box.top, box.right - right, box.bottom - bottom, left - box.left].map(
      (px) => Math.max(0, px / scale),
    );
    if (inset.some((px) => px > 0.5)) {
      overlay.style.clipPath = `inset(${inset.map((px) => `${px}px`).join(" ")})`;
    }
  }

  private render(): void {
    if (!this.bars) {
      return;
    }
    const visible = this.isVisible();
    this.overlay!.toggleAttribute("data-corner", this.cornered);
    for (const axis of ["y", "x"] as Axis[]) {
      const { strip, thumb } = this.bars[axis];
      const geometry = visible && this.overflow[axis] ? this.thumbGeometry(axis) : null;
      // An area too small for a thumb gets none.
      const shown = !!geometry && geometry.track >= MIN_THUMB;
      strip.toggleAttribute("data-visible", shown);
      strip.toggleAttribute(
        "data-active",
        this.overlayHovered === axis || this.drag?.axis === axis,
      );
      if (geometry && shown) {
        const { size, offset } = geometry;
        if (axis === "y") {
          thumb.style.height = `${size}px`;
          thumb.style.transform = `translateY(${offset}px)`;
        } else {
          thumb.style.width = `${size}px`;
          thumb.style.transform = `translateX(${offset}px)`;
        }
      }
    }
  }

  /** The thumb's length and its offset along the track, in px. */
  private thumbGeometry(axis: Axis): { size: number; offset: number; track: number } {
    const el = this.el;
    const visible = axis === "y" ? el.clientHeight : el.clientWidth;
    const content = axis === "y" ? el.scrollHeight : el.scrollWidth;
    const maxScroll = content - visible;
    const track = visible - INSET * 2 - (this.cornered ? STRIP : 0);
    const size = Math.min(track, Math.max(MIN_THUMB, (track * visible) / content));
    let progress = maxScroll > 0 ? this.scrollPosition(axis) / maxScroll : 0;
    // Right-to-left: scrollLeft runs from 0 (start, at the right) down to -maxScroll.
    if (axis === "x" && this.rtl) {
      progress = 1 + progress;
    }
    return { size, offset: INSET + (track - size) * progress, track };
  }

  private scrollPosition(axis: Axis): number {
    return axis === "y" ? this.el.scrollTop : this.el.scrollLeft;
  }

  private onThumbDown(axis: Axis, event: PointerEvent): void {
    // Not a second pointer while one drags: it would take the drag's user-select for the element's.
    if (event.button || this.drag) {
      return;
    }
    event.preventDefault();
    const { thumb } = this.bars![axis];
    thumb.setPointerCapture?.(event.pointerId);
    const { size, track } = this.thumbGeometry(axis);
    const visible = axis === "y" ? this.el.clientHeight : this.el.clientWidth;
    const content = axis === "y" ? this.el.scrollHeight : this.el.scrollWidth;
    this.drag = {
      axis,
      pointerId: event.pointerId,
      pointer: axis === "y" ? event.clientY : event.clientX,
      scroll: this.scrollPosition(axis),
      ratio: (content - visible) / Math.max(1, track - size),
    };
    this.userSelect = this.el.style.userSelect;
    this.el.style.userSelect = "none";
    this.render();
  }

  private onThumbMove(event: PointerEvent): void {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }
    const pointer = drag.axis === "y" ? event.clientY : event.clientX;
    const scroll = drag.scroll + (pointer - drag.pointer) * drag.ratio;
    // Instant whatever the element's scroll-behavior, or the content would trail the thumb.
    this.el.scrollTo({ [drag.axis === "y" ? "top" : "left"]: scroll, behavior: "instant" });
  }
}
